import { Brand, BrandId } from 'utils/override';
import {
  ComponentCustomerReviewsBlockReview,
  EnumCustomerReviewsBlockBgColorVariant,
  EnumCustomerReviewsBlockReviewSource,
} from '$/(cms)/_modules/cmsService/types';
import { toCmsAbsUrl } from '$/(cms)/_modules/routing';
import {
  BG_COLOR_CLASS_MAP,
  BRAND_DISPLAY_NAMES,
  REVIEW_SOURCE_LABEL,
} from '$/components/cms/components-user/CustomerReviewsBlock/constants';

export const clampRating = (rating?: number) => {
  if (typeof rating !== 'number') {
    return undefined;
  }

  if (rating < 0) return 0;
  if (rating > 5) return 5;
  return rating;
};

export const getBackgroundClass = (
  bg?: EnumCustomerReviewsBlockBgColorVariant,
): string => {
  if (!bg) {
    return 'bg-fill/primary-light';
  }

  return BG_COLOR_CLASS_MAP[bg];
};

export const getSourceLabel = (
  source?: EnumCustomerReviewsBlockReviewSource,
): string => {
  if (!source) {
    return REVIEW_SOURCE_LABEL[
      EnumCustomerReviewsBlockReviewSource.GoogleReviews
    ];
  }

  return REVIEW_SOURCE_LABEL[source] ?? 'Google';
};

// TODO: move to strapi, if Kamil agrees
export const getSourceLabelUrl = (
  brand: BrandId,
  source?: EnumCustomerReviewsBlockReviewSource,
): string => {
  if (source === EnumCustomerReviewsBlockReviewSource.GoogleReviews) {
    switch (brand) {
      case 'singsaver':
        return 'https://www.google.com/maps/place/SingSaver/@1.2739122,103.8436426,17z/data=!3m1!5s0x31da196846e877a5:0x97e64648a963678!4m8!3m7!1s0x31da196d9bbc0325:0x277692c2612678fc!8m2!3d1.2739068!4d103.8462175!9m1!1b1!16s%2Fg%2F11bvvxkfrc?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoASAFQAw%3D%3D';
      case 'moneyhero':
        return 'https://www.google.com/maps/place/MoneyHero+%7C+%E6%AF%94%E8%BC%83%E7%86%B1%E9%96%80%E4%BF%A1%E7%94%A8%E5%8D%A1%E3%80%81+%E7%A7%81%E4%BA%BA%E8%B2%B8%E6%AC%BE%EF%B9%91%E4%BF%9D%E9%9A%AA%E5%8F%8A%E5%85%B6%E4%BB%96%E9%87%91%E8%9E%8D%E7%94%A2%E5%93%81/data=!4m7!3m6!1s0x340400ffce2f559b:0xe8c7775f3218f28a!8m2!3d22.2776363!4d114.1837672!16s%2Fg%2F11bwfkys5h!19sChIJm1Uvzv8ABDQRivIYMl93x-g?authuser=0&hl=en&rclk=1';
      case 'moneymax':
        return 'https://www.google.com/maps/place/Moneymax/@14.561776,121.0200497,17z/data=!3m1!4b1!4m6!3m5!1s0x3397c908ce28ea27:0x70a8a38d54a78402!8m2!3d14.5617708!4d121.0226246!16s%2Fg%2F11b6rvb9lt?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoASAFQAw%3D%3D';
      case 'money101':
        return 'https://www.google.com/maps/place/Money101/@23.4857501,80.4762125,4z/data=!4m6!3m5!1s0x3442abc983bcd4f1:0x2b83c66db037f4d5!8m2!3d23.4857501!4d119.49965!16s%2Fg%2F11b6fk4trr?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoASAFQAw%3D%3D';
    }
  }

  return '#';
};

export const transformSeoData = (
  brand: keyof Brand<unknown>,
  host: string,
  ratingValuesCount: number,
  reviews: ComponentCustomerReviewsBlockReview[],
  sourceLabel: string,
  aggregateRatingValue?: number,
) => {
  if (!reviews.length) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    // Same node id the Strapi `seo_organisation_data` Organization carries (H-ORG template), so the
    // two Organizations on a page read as one entity (CMS-2098).
    '@id': toCmsAbsUrl(host, '/#organization'),
    name: BRAND_DISPLAY_NAMES[brand] ?? 'SingSaver',
    aggregateRating:
      aggregateRatingValue && ratingValuesCount
        ? {
            '@type': 'AggregateRating',
            ratingValue: aggregateRatingValue,
            reviewCount: ratingValuesCount,
            bestRating: 5,
            worstRating: 1,
          }
        : undefined,
    review: reviews.map((review, index) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.reviewerName ?? 'Verified Reviewer',
      },
      name:
        review.reviewTitle ??
        `Customer review ${String(index + 1).padStart(2, '0')}`,
      reviewBody: review.reviewBody,
      reviewRating:
        typeof review.rating === 'number'
          ? {
              '@type': 'Rating',
              ratingValue: clampRating(review.rating),
              bestRating: 5,
              worstRating: 1,
            }
          : undefined,
      publisher: {
        '@type': 'Organization',
        name: `${sourceLabel} Reviews`,
      },
    })),
  };
};
